import { ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { notify } from "@/modules/notify/notify";
import { anonymousRouting } from "@/modules/router/routings/anonymousRouting";

import { UserRoles } from "../api/resources/company/companyResourceStruct";
import { authContext, Company } from "../contexts/AuthContext";
import { useGetCompaniesQuery } from "../hooks/useGetCompaniesQuery";

const localStorageUserIdKey = "userId";
const localStorageSelectedCompanyIdKey = "selectedCompanyId";

interface AuthContextProviderProps {
  children?: ReactNode;
}

const translationNamespace = "contexts/AuthContextProvider";

export function AuthContextProvider(props: AuthContextProviderProps) {
  const { children } = props;

  const history = useHistory();
  const { t } = useTranslation(translationNamespace);

  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userCompanyId, setUserCompanyId] = useState<string | null>(null);
  const [userCompany, setUserCompany] = useState<Company | null>(null);
  const [hasAdminPrivilages, setHasAdminPrivilages] = useState(false);
  const {
    data: companiesData,
    refetch: refetchCompaniesData,
    isFetching: isFetchingCompaniesData,
  } = useGetCompaniesQuery(userId ? { userId } : { enabled: false });

  const signIn = useCallback(({ id }: { id: string }) => {
    localStorage.setItem(localStorageUserIdKey, id);
    setUserId(id);
  }, []);

  const signOut = useCallback(() => {
    localStorage.clear();

    const basename =
      document.head.querySelector("base")?.getAttribute("href") || "/";

    const base = basename.endsWith("/") ? basename.slice(0, -1) : basename;
    const signInPath = `${base}${anonymousRouting.buildUrl("signIn")}`;
    window.location.href = new URL(signInPath, window.location.origin).href;
  }, []);

  const setPrivilages = (access: UserRoles[]) => {
    if (access.includes("ADMIN") || access.includes("OWNER")) {
      setHasAdminPrivilages(true);
    }
  };

  useEffect(() => {
    const userId = localStorage.getItem(localStorageUserIdKey);
    const userCompanyId = localStorage.getItem(
      localStorageSelectedCompanyIdKey
    );
    setUserId(userId);
    setUserCompanyId(userCompanyId);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (userCompanyId) {
      localStorage.setItem(localStorageSelectedCompanyIdKey, userCompanyId);
    } else if (userId) {
      void refetchCompaniesData();
    }
  }, [userCompanyId, userId, refetchCompaniesData]);

  useEffect(() => {
    if (companiesData) {
      // if user doesn't have a company redirect to the login and display a toast
      if (!companiesData.data.length) {
        localStorage.clear();
        setUserId(null);
        setUserCompany(null);
        notify.alert.error(
          t(
            "You are not added to any company. Please contact the administrator"
          )
        );
        history.replace(anonymousRouting.buildUrl("signIn"));
        return;
      }
      const { id, attributes, meta } = companiesData.data[0];
      // for now we use only first company
      setUserCompanyId(id);
      setUserCompany({
        name: attributes.name,
        role: meta.access,
      });
      setPrivilages(meta.access);
      setIsLoading(false);
    }
  }, [companiesData, history, t]);

  useEffect(() => {
    if (isFetchingCompaniesData) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isFetchingCompaniesData]);

  return (
    <authContext.Provider
      value={{
        userId,
        userCompanyId,
        userCompany,
        hasAdminPrivilages,
        isLoading,
        signIn,
        signOut,
      }}
    >
      {children}
    </authContext.Provider>
  );
}
